import React, { Fragment, useEffect, useState } from 'react';
import { din276_18 } from './din276-data/din276-18';
import { din276_08 } from './din276-data/din276-08';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import TypeMoney from '../../form/form-fields/TypeMoney';
import MoneyLabel from '../../label/MoneyLabel';
import { updateOrCreate } from '../../special/updateOrCreate';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { tokens } from '../../../global/theme/tokens';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import InfoIcon from '@mui/icons-material/Info';
import TypeText from '../../form/form-fields/TypeText';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../../api/api';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { DINLoader } from './DINLoader';
import ExoDialog from '../ExoDialog';
import TypeCustomSelect from '../../form/form-fields/TypeCustomSelect';

export const CostAllocator = ({
  startData,
  noControls,
  onClose,
  onSubmit,
  dinTitle,
  hoaiTitle,
  dinId,
  startDinYear,
  shownCostGroups, //All are shown if null. In 100s
  disabledCostGroups, //Enabling only available if at least one is disabled. In 100s
  additionalFields, //additional fields from HOAI but not from DIN276
  creditableCostFunction, //Function to calculate Creditable Costs using entries and additionalFields as input
}) => {
  const dins = [din276_18, din276_08]; //In case of new DIN import it and add it to this array
  //sort in reverse order (newest should have 0 array index)

  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const { t } = useTranslation();

  const [disabledGroups, setDisabledGroups] = useState(
    disabledCostGroups ? new Set(disabledCostGroups) : null
  );

  function getDinIndexByYear(year) {
    if (!year) return null;
    return dins.findIndex((el) => el.year == year);
  }

  const [dinIndex, setDinIndex] = useState(
    getDinIndexByYear(startDinYear) || 0
  );
  const [din276, setDin276] = useState(dins[dinIndex]);

  const [openLoad, setOpenLoad] = useState(false);

  const [arrOfEntries, setArrOfEntries] = useState([]);
  const [arrOfAdditionalFields, setArrOfAdditionalFields] = useState(
    additionalFields ? [] : null
  );

  const [sums, setSums] = useState({
    nettoCosts: 0,
    costs: 0 /*, percent: 0*/,
  });
  const [titleCollection, setTitleCollection] = useState({
    title: dinTitle ?? hoaiTitle ?? '',
  });
  const [id, setId] = useState(dinId);

  function generateDINOptions() {
    return dins.map((elem, index) => {
      return { label: elem.year, value: index };
    });
  }

  const dinVersionFields = {
    label: t('DIN276 version'),
    key: 'dinIndex',
    options: generateDINOptions(),
    noUnselect: true,
    disabled: Boolean(shownCostGroups),
  };

  useEffect(() => {
    if (!startData || startData.length == 0)
      return setArrOfEntries(calculateBaseEntries(startData));
    const data = uncompress(startData);
    setSums(calculateSums(data));
    setArrOfEntries([...data]);
  }, [startData]);

  function calculateSums(entries, additionalFields, disabledGroupsArray) {
    const disabled = disabledGroupsArray ?? disabledGroups;

    const cleanedEntries = disabled
      ? entries.filter(
          (el) =>
            !disabled?.has(el.number) && shownCostGroups.includes(el.number)
        )
      : entries;

    const additionalFieldsLocal = additionalFields ?? arrOfAdditionalFields;

    const cleanedAdditionalFields = additionalFieldsLocal
      ? disabled
        ? additionalFieldsLocal.filter((el) => !disabled?.has(el.key))
        : additionalFieldsLocal
      : [];

    const nettoCosts = cleanedEntries.reduce((acc, entry) => {
      acc += entry.nettoCosts ? Number(entry.nettoCosts) : 0;
      return acc;
    }, 0);

    let costs = 0; //Creditable Costs Function here

    if (creditableCostFunction) {
      costs = creditableCostFunction(cleanedEntries, cleanedAdditionalFields);
    } else {
      costs = entries.reduce((acc, entry) => (acc += entry.costs ?? 0), 0);
    }

    /*const percent = nettoCosts
      ? Math.round(100 * (costs / nettoCosts) * 100) / 100
      : 0;
    */
    return { nettoCosts, costs /*, percent */ };
  }

  function handleEntryChange(newEntry) {
    const newArrOfEntries = updateOrCreate(arrOfEntries, newEntry);

    setArrOfEntries(newArrOfEntries);

    setSums(calculateSums(newArrOfEntries));
  }

  function handleAdditionalFieldsChange(newEntry) {
    const newArrOfAdditionalFields = updateOrCreate(
      arrOfAdditionalFields,
      newEntry,
      false,
      'key'
    );

    setArrOfAdditionalFields(newArrOfAdditionalFields);

    setSums(calculateSums(arrOfEntries, newArrOfAdditionalFields));
  }

  function calculateBaseEntries(startData, din) {
    if (startData) {
      return startData;
    }
    din = din ?? din276;
    var newBaseEntries = [];
    din.data.forEach((element) => {
      newBaseEntries.push({ ...element, id: element.number });
    });
    return newBaseEntries;
  }

  function compressData(arrOfEntries) {
    if (!arrOfEntries) return null;

    return arrOfEntries.map((el) => {
      //if (!el.percent && !el.nettoCosts) return;

      const entry = {
        number: el.id || el.number,
        nettoCosts: el.nettoCosts,
        costs: el.costs,
        //percent: el.percent,
        phases: compressData(el.phases),
      };

      return Object.fromEntries(
        Object.entries(entry).filter(([_, value]) => value != null)
      );
    });
  }

  function uncompress(data, din) {
    din = din ?? din276;
    return uncompressHelper(din.data, data);
  }

  function uncompressHelper(dinData, data) {
    if (!dinData) return;
    if (!data) return dinData;

    return dinData.map((el, index) => {
      return {
        number: el.number || el.id,
        id: el.number || el.id,
        label: el.label,
        description: el.description,
        nettoCosts: data[index]?.nettoCosts ?? null,
        costs: data[index]?.costs ?? null,
        //percent: data[index]?.percent ?? null,
        phases: uncompressHelper(el.phases, data[index]?.phases),
      };
    });
  }

  function handleSubmit() {
    const data = compressData(arrOfEntries);

    DBRequest({
      config,
      path: id ? `din/${id}` : `din`,
      method: id
        ? API_PATCH({
            totalCost: sums.costs,
            title: titleCollection.title,
            dinData: data,
            year: String(din276.year),
          })
        : API_POST({
            totalCost: sums.costs,
            title: titleCollection.title,
            dinData: data,
            year: String(din276.year),
          }),
      onResponse: handleResponse,
    });
  }

  function handleResponse(res) {
    onSubmit({
      baseAmount: res.data.totalCost,
      dinData: res.data.dinData,
      dinTitle: res.data.title,
      dinId: res.data.id,
      year: res.data.year,
    });
    onClose();
  }

  function handleTitleChange(key, value) {
    setTitleCollection({ title: value });
  }

  function setLoadedDin(data) {
    setId(data.data.id);
    const uncompressedData = uncompress(
      data.data.dinData,
      dins[getDinIndexByYear(data.data.year)]
    );

    setDin276(dins[getDinIndexByYear(data.data.year)]);
    setDinIndex(getDinIndexByYear(data.data.year));

    setTitleCollection({ title: data.data.title });
    setSums(calculateSums(uncompressedData));
    setArrOfEntries([...uncompressedData]);
    setOpenLoad(false);
  }

  function handleDinChange(key, newDinIndex) {
    setDin276(dins[newDinIndex]);
    setArrOfEntries(calculateBaseEntries(null, dins[newDinIndex]));
    setSums({ nettoCosts: 0, costs: 0 /*, percent: 0*/ });
    setDinIndex(newDinIndex);
  }

  function handleDisabledChange(number, disabled) {
    if (!disabledGroups) return;
    let newDisabledGroups = new Set(disabledGroups);
    if (disabled) {
      newDisabledGroups.add(String(number));
    } else {
      newDisabledGroups.delete(String(number));
    }
    setDisabledGroups(newDisabledGroups);
    setSums(
      calculateSums(arrOfEntries, arrOfAdditionalFields, newDisabledGroups)
    );
  }

  return (
    <Box className="p-5">
      <ExoDialog open={openLoad} onClose={() => setOpenLoad(false)}>
        <DINLoader onSelect={setLoadedDin} />
      </ExoDialog>
      <Box className="flex justify-between pb-5">
        <Typography fontSize={24} fontWeight={500}>
          {t('DIN 276')}
        </Typography>
        <IconButton onClick={() => setOpenLoad(true)}>
          <CloudDownloadIcon />
        </IconButton>
      </Box>
      <Box className="my-5">
        <TypeCustomSelect
          field={dinVersionFields}
          dataCollection={{ dinIndex: dinIndex }}
          errors={{}}
          updateValidationOnChange={handleDinChange}
        />
      </Box>
      <Box
        className="w-full"
        sx={{
          backgroundColor: colors.glass,

          '& tr.tableRow:hover:not(.extendedTable) td': {
            background: `linear-gradient(${colors.selected}40, ${colors.selected}40), linear-gradient(${colors.glass}, ${colors.glass})`,
          },
          '& .selectedRow td': {
            background: `linear-gradient(${colors.selected}40, ${colors.selected}40), linear-gradient(${colors.glass}, ${colors.glass})`,
          },
        }}
      >
        <table className="w-full">
          <tbody>
            <tr>
              <th className="px-2"></th>
              <th className="px-2 text-left">
                <Typography>{t('Cost group')}</Typography>
              </th>
              <th></th>
              <th className="px-2">
                <Typography>{t('Costs')}</Typography>
              </th>
              {/* <th className="px-2">
                <Typography>{t('Creditable portion')}</Typography>
              </th>*/}
              <th className="px-2 text-right">
                <Typography>{t('Creditable Costs')}</Typography>
              </th>
            </tr>
            <tr>
              <td></td>
              <td className="px-2">
                {/*<Typography fontWeight={600}>{t('Sum')}</Typography>*/}
              </td>
              <td></td>
              <td>
                <Typography className="text-center" fontWeight={600}>
                  <MoneyLabel money={sums.nettoCosts} />
                </Typography>
              </td>
              {/*<td className="w-[120px]">
                <Typography className="text-center" fontWeight={600}>
                  {sums.percent + '%'}
                </Typography>
              </td>*/}
              <td className="w-[120px] px-2">
                <Typography className="text-right" fontWeight={600}>
                  <MoneyLabel money={sums.costs} />
                </Typography>
              </td>
            </tr>
            <tr>
              <td className="py-[5px]" colSpan={999}>
                <Divider />
              </td>
            </tr>
            {arrOfEntries.map(
              (group, index) =>
                (!shownCostGroups ||
                  shownCostGroups.includes(group.number)) && (
                  <Fragment key={group.number}>
                    {index != 0 && false && (
                      <td colSpan={99}>
                        <Divider />
                      </td>
                    )}
                    <Row
                      key={group.number}
                      group={group}
                      onChange={handleEntryChange}
                      din276={din276}
                      disabled={disabledGroups?.has(group.number)}
                      toggleDisabled={handleDisabledChange}
                    />
                  </Fragment>
                )
            )}
            {additionalFields &&
              additionalFields.map((field) => (
                <Row
                  key={field.key}
                  group={field}
                  onChange={handleAdditionalFieldsChange}
                  additionalFieldKey={field.key}
                  disabled={disabledGroups?.has(field.key)}
                  toggleDisabled={handleDisabledChange}
                />
              ))}
          </tbody>
        </table>
      </Box>
      {!noControls && (
        <Box className="flex justify-between px-9 py-5">
          <Button variant="contained" color="error" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Box className="flex items-center gap-2">
            <TypeText
              size="small"
              field={{ label: t('Save as'), key: 'title' }}
              dataCollection={titleCollection}
              errors={{}}
              updateValidationOnChange={handleTitleChange}
            />
            <Button
              className="h-full"
              variant="contained"
              color="success"
              onClick={handleSubmit}
            >
              {t('Save')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const Row = ({
  onChange,
  group,
  din276,
  disabled,
  toggleDisabled,
  additionalFieldKey,
}) => {
  const { t } = useTranslation();

  const [dataCollection, setDataCollection] = useState(
    group.nettoCosts /*|| group.percent*/
      ? group
      : {
          ...group,
          nettoCosts: 0,
          //percent: 100,
        }
  );
  const [open, setOpen] = useState(false);

  const [childEntries, setChildEntries] = useState([]);

  useEffect(() => {
    setOpen(false);
  }, [din276]);

  useEffect(() => {
    if (group.phases || group.phases?.length > 0) {
      const newDataCollection = group.nettoCosts /*|| group.percent*/
        ? group
        : {
            ...group,
            nettoCosts: 0,
            //percent: 100,
          };
      setChildEntries(calculateChildEntries(group, newDataCollection));
    }
  }, [group, group?.phases, /*group?.percent,*/ group?.nettoCosts]);

  function calculateChildEntries(group, newDataCollection) {
    var newChildren = [];

    var hasChildren = false;
    group.phases.forEach((element) => {
      if (/*element.percent ||*/ element.nettoCosts) hasChildren = true;
      newChildren.push({ ...element, id: element.number });
    });

    if (hasChildren)
      setDataCollection({
        ...dataCollection,
        showPhases: true,
        ...newDataCollection,
      });
    else if (newDataCollection) {
      setDataCollection(newDataCollection);
    }
    return newChildren;
  }

  function resetChildren(group) {
    let newGroup = null;
    if (group.number % 100 == 0) {
      newGroup = din276.data.find((el) => el.number == group.number);
    } else if (group.number % 10 == 0) {
      const parentNum = Number(group.number.toString()[0]) * 100;
      newGroup = din276.data.find((el) => el.number == parentNum);
      newGroup = newGroup.phases.find((el) => el.number == group.number);
    }
    return calculateChildEntries(newGroup);
  }

  function handleEntryChange(updatedEntry) {
    const newChildren = updateOrCreate(childEntries, updatedEntry);

    const { costs, nettoCosts /*, percent*/ } =
      calculateSumOverChildren(newChildren);

    // update entry
    const newDataCollection = {
      ...dataCollection,
      costs,
      nettoCosts,
      phases: newChildren,
      //percent,
    };

    onChange({ ...group, ...newDataCollection });
    setDataCollection(newDataCollection);
    setChildEntries(newChildren);
  }

  function calculateSumOverChildren(children) {
    var costs = 0;
    var nettoCosts = 0;
    // calc new entry
    children.forEach((element) => {
      if (element.costs)
        // anrechenbare kosten
        costs += parseFloat(element.costs);
      if (element.nettoCosts)
        // netto kosten
        nettoCosts += parseFloat(element.nettoCosts);
    });

    /*const percent = dataCollection.nettoCosts
      ? Math.round(
          100 * (dataCollection.costs / dataCollection.nettoCosts) * 100
        ) / 100
      : 0;*/

    return { costs, nettoCosts /*, percent*/ };
  }

  function togglePhases() {
    const newChildren = calculateChildEntries(group);

    const { costs, nettoCosts /*, percent*/ } =
      calculateSumOverChildren(childEntries);

    const newDataCollection = {
      ...dataCollection,
      showPhases: !dataCollection.showPhases,
      costs: !dataCollection.showPhases ? 0 : costs,
      nettoCosts: !dataCollection.showPhases ? 0 : nettoCosts,
      //percent: !dataCollection.showPhases ? 0 : percent,
      phases: !dataCollection.showPhases ? newChildren : dataCollection.phases,
    };

    if (!dataCollection.showPhases) {
      setChildEntries(resetChildren(group));
      onChange({ ...group, ...newDataCollection });
    }

    setDataCollection(newDataCollection);
    setOpen(!dataCollection.showPhases);
  }

  function handleRowChange(key, value) {
    const newData = { ...dataCollection, [key]: value };
    const costs = calculateCosts(newData);
    setDataCollection({ ...newData, costs });
    onChange({ ...group, ...newData, costs });
  }

  function calculateCosts(data) {
    const costs = data.nettoCosts // && data.percent
      ? parseFloat(data.nettoCosts) //* (parseFloat(data.percent) / 100)
      : 0;

    return costs;
  }

  function handleToggleDisabled() {
    if (!disabled) {
      setOpen(false);
    }
    toggleDisabled(group.number ?? additionalFieldKey, !disabled);
  }

  return (
    <>
      <tr>
        <td
          className="w-[50px] px-2"
          style={{
            paddingLeft:
              group.number % 100 == 0 ? 10 : group.number % 10 == 0 ? 35 : 60,
          }}
        >
          {group.number && (
            <Tooltip
              title={
                dataCollection.showPhases
                  ? t('Add next lower level')
                  : t('Remove next lower level')
              }
            >
              <IconButton
                size="small"
                onClick={togglePhases}
                disabled={disabled}
              >
                {group.phases &&
                  (dataCollection.showPhases ? (
                    <RemoveCircleIcon fontSize="small" />
                  ) : (
                    <AddCircleIcon fontSize="small" />
                  ))}
              </IconButton>
            </Tooltip>
          )}
        </td>
        <td className="px-2">
          <Box
            onClick={toggleDisabled ? handleToggleDisabled : () => {}}
            sx={{ cursor: toggleDisabled ? 'pointer' : 'default' }}
          >
            <Typography
              sx={{ textDecoration: disabled ? 'line-through' : 'none' }}
            >
              {(group.number ?? '') + (group.number ? ' ' : '') + group.label}
            </Typography>
          </Box>
        </td>
        <td className="w-[50px]">
          {group.description && (
            <Tooltip title={group.description}>
              <InfoIcon />
            </Tooltip>
          )}
        </td>
        <td className="w-[160px] px-2">
          {dataCollection.showPhases ? (
            <MoneyLabel money={dataCollection.nettoCosts} />
          ) : (
            <TypeMoney
              field={{ label: '', key: 'nettoCosts' }}
              dataCollection={dataCollection}
              errors={{}}
              size={'small'}
              updateValidationOnChange={handleRowChange}
            />
          )}
        </td>
        {/*
        <td className="w-[120px] px-2">
          {dataCollection.showPhases ? (
            <Typography>{dataCollection.percent + '%'}</Typography>
          ) : (
            <TypeNumber
              field={{ label: '', key: 'percent', unit: '%' }}
              dataCollection={dataCollection}
              errors={{}}
              size={'small'}
              updateValidationOnChange={handleRowChange}
            />
          )}
        </td>*/}
        <td className="w-[120px] px-2 text-right">
          <MoneyLabel money={disabled ? 0 : dataCollection.costs ?? 0} />
        </td>
        <td className="w-[50px]">
          {childEntries && dataCollection.showPhases && (
            <IconButton
              size={group.number % 100 == 0 ? 'small' : 'medium'}
              onClick={() => setOpen(!open)}
              disabled={disabled}
            >
              {open ? (
                <KeyboardArrowDownIcon
                  fontSize={group.number % 100 == 0 ? 'medium' : 'small'}
                />
              ) : (
                <KeyboardArrowRightIcon
                  fontSize={group.number % 100 == 0 ? 'medium' : 'small'}
                />
              )}
            </IconButton>
          )}
        </td>
      </tr>
      <tr className="extendedTable">
        <td colSpan={999}>
          {open && (
            <Box className="py-2">
              <Divider />
            </Box>
          )}
          <Collapse in={open}>
            {open && (
              <table className="w-full">
                <tbody>
                  {childEntries?.map((element) => (
                    <Row
                      key={element.number}
                      onChange={handleEntryChange}
                      group={element}
                      startData={childEntries.find(
                        (el) => element.number === el.number
                      )}
                      din276={din276}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </Collapse>
          {open && (
            <Box className="py-2">
              <Divider />
            </Box>
          )}
        </td>
      </tr>
    </>
  );
};
